import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { payslipsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { formatDate, formatMoney, orDash } from '@/utils/format';
import { Days, SourceBadge } from './shared';

/**
 * A printable payslip.
 *
 * <p>Every figure here was snapshotted when the run happened — the employee's
 * name and department, the package's name, each component's name and amount.
 * Nothing is joined back to the live record, so a payslip from three years ago
 * reads as it did on the day.
 *
 * <p>The print rules live in the stylesheet under {@code @media print}: the
 * chrome disappears and the slip prints on its own.
 */
export function PayslipPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();

  // Somebody reading their own payslip has no PAYSLIP_VIEW; the self-service
  // endpoint is the one they can reach, and it also refuses to show a
  // provisional slip from an unapproved run.
  const readsAny = can(PERMISSIONS.PAYSLIP_VIEW);

  const payslip = useQuery({
    queryKey: queryKeys.payslips.detail(id),
    queryFn: () => (readsAny ? payslipsApi.get(id) : payslipsApi.mineById(id)),
    enabled: id !== '',
  });

  if (payslip.isError) {
    return <ErrorState error={payslip.error} onRetry={() => void payslip.refetch()} />;
  }
  if (payslip.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const slip = payslip.data;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Link
          to={readsAny ? ROUTES.payrollDetail(slip.periodId) : ROUTES.myPayslips}
          className="inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {readsAny ? 'Back to the run' : 'My payslips'}
        </Link>
        <Button variant="secondary" onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print
        </Button>
      </div>

      {/* `payslip` is what the print rules key on: everything else is hidden. */}
      <article className="payslip mx-auto max-w-3xl border border-line bg-bg p-6 print:border-0 print:p-0">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-line pb-4">
          <div>
            <h1 className="text-lg font-extrabold uppercase tracking-[0.08em] text-ink">Payslip</h1>
            <p className="mt-0.5 font-mono text-[12px] text-muted">{slip.slipNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-ink">{slip.periodName}</p>
            <p className="text-[12px] text-muted">
              {formatDate(slip.periodStart)} – {formatDate(slip.periodEnd)}
            </p>
            <p className="text-[12px] text-muted">Paid {formatDate(slip.paymentDate)}</p>
          </div>
        </header>

        <section className="grid gap-x-8 gap-y-1 border-b border-line py-4 sm:grid-cols-2">
          <Detail label="Employee" value={slip.employeeName} />
          <Detail label="Employee number" value={orDash(slip.employeeNumber)} />
          <Detail label="Designation" value={orDash(slip.designation)} />
          <Detail label="Department" value={orDash(slip.departmentName)} />
          <Detail label="Salary package" value={orDash(slip.structureName)} />
          <Detail
            label="Days paid"
            value={
              <>
                <Days value={slip.payableDays} /> of {slip.workingDays}
                {slip.unpaidLeaveDays > 0 && (
                  <> (<Days value={slip.unpaidLeaveDays} /> unpaid leave)</>
                )}
              </>
            }
          />
        </section>

        <div className="grid gap-6 py-4 sm:grid-cols-2">
          <LineTable
            title="Earnings"
            lines={slip.earnings}
            total={slip.grossEarnings}
            totalLabel="Gross earnings"
          />
          <LineTable
            title="Deductions"
            lines={slip.deductions}
            total={slip.totalDeductions}
            totalLabel="Total deductions"
          />
        </div>

        <footer className="border-t-2 border-line pt-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-extrabold uppercase tracking-[0.06em] text-ink">
              Net pay
            </span>
            <span
              className="text-2xl font-extrabold text-ink"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatMoney(slip.netPay)}
            </span>
          </div>
          {slip.netPayInWords !== undefined && (
            <p className="mt-1 text-right text-[12px] italic text-muted">{slip.netPayInWords}</p>
          )}

          {slip.paymentReference !== undefined && (
            <p className="mt-3 text-[12px] text-muted">
              Payment reference: <span className="font-mono text-ink">{slip.paymentReference}</span>
            </p>
          )}

          <p className="mt-4 text-[11px] text-subtle">
            Computer generated. Every figure was recorded when the payroll run was calculated and
            does not change afterwards.
          </p>
        </footer>
      </article>
    </div>
  );
}

function LineTable({
  title,
  lines,
  total,
  totalLabel,
}: {
  title: string;
  lines: {
    id: string;
    code: string;
    name: string;
    amount: number;
    source: Parameters<typeof SourceBadge>[0]['source'];
    remarks?: string;
  }[];
  total: number;
  totalLabel: string;
}) {
  return (
    <section>
      <h2 className="border-b border-line pb-1 text-[11px] font-extrabold uppercase tracking-[0.06em] text-muted">
        {title}
      </h2>
      <dl className="mt-1">
        {lines.map((line) => (
          <div key={line.id} className="border-b border-line py-1.5 last:border-0">
            <div className="flex items-baseline justify-between gap-3">
              <dt className="min-w-0 text-[13px] text-ink">{line.name}</dt>
              <dd
                className="shrink-0 text-[13px] text-ink"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatMoney(line.amount)}
              </dd>
            </div>
            {(line.source !== 'STRUCTURE' || line.remarks !== undefined) && (
              <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
                <SourceBadge source={line.source} />
                {line.remarks}
              </p>
            )}
          </div>
        ))}
      </dl>
      <div className="mt-1 flex items-baseline justify-between border-t-2 border-line pt-1.5">
        <span className="text-[12px] font-bold uppercase tracking-[0.04em] text-muted">
          {totalLabel}
        </span>
        <span
          className="text-sm font-extrabold text-ink"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatMoney(total)}
        </span>
      </div>
    </section>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[11px] uppercase tracking-[0.04em] text-muted">{label}</span>
      <span className="text-[13px] text-ink">{value}</span>
    </div>
  );
}
